import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import Constructions from '../constructions-step/constructions/constructions.component';
import PatientSummary from '../../shared/confirm/patient-summary/patient-summary.component';
import DeliverySummary from '../../shared/confirm/delivery-summary/delivery-summary.component';
import SampleSummary from '../../shared/confirm/sample-summary/sample-summary.component';
import { createOrderTemplate, modifyOrderTemplate } from 'apollo/templates/order/mutation.graphql';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import FileGrid from 'components/shared/file-upload-area/file-grid.component';
import { OrderInputType } from 'models/order-input/shared';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';
import SupplierSummary from './supplier-summary/supplier-summary.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { supplierOrderToOrderInput } from 'components/order-flow/shared/order-input-helper';

const ConfirmStep: FC = () => {
    const { t: translate } = useTranslation(['common', 'orders', 'client-directory']);
    const { order } = useContext(SupplierOrderContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isResend, setIsResend] = useState(false);
    const search = useLocation().search;
    const navigate = useNavigate();
    const currentOrg = currentOrgVar();
    const { setAppToasterContent } = useContext(AppToasterContext);

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        setIsResend(queryParams.get('isResend') === 'true' || false);
    }, [search]);

    const [createOrder, { loading: createLoading }] = useMutation(createOrderTemplate, {
        onCompleted: ({ createOrder }) => {
            navigate(`/order-details/${createOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_CREATED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [modifyOrder, { loading: modifyLoading }] = useMutation(modifyOrderTemplate, {
        onCompleted: ({ modifyOrder }) => {
            navigate(`/order-details/${modifyOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_MODIFIED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const onSubmitOrderClick = () => {
        const orderInput = supplierOrderToOrderInput(order, currentOrg._id as string);
        if (order.inputType !== OrderInputType.NEW) {
            modifyOrder({ variables: { orderId: order._id, order: orderInput, isResend } });
            return;
        }
        createOrder({ variables: { order: orderInput } });
    };

    return (
        <Fragment>
            {order ? (
                <Box>
                    <Box mb={2}>
                        <Typography variant="h1">{translate('orders:CONFIRM')}</Typography>
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ height: '100%' }}>
                                    <Box p={2}>
                                        <Typography variant="h4">{translate('orders:PATIENT')}</Typography>
                                        <Box mt={1}>
                                            <PatientSummary patient={order.patient} />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ height: '100%' }}>
                                    <Box p={2}>
                                        <Typography variant="h4">{translate('client-directory:SUPPLIER')}</Typography>
                                        <Box mt={1}>
                                            <SupplierSummary
                                                supplier={order.supplier}
                                                responsibleUser={order.dentist}
                                            />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box>
                        <Constructions disableEdit />
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <DeliverySummary order={order} />
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="h4">{translate('orders:SAMPLE_AND_PACKAGE_CONTENT')}</Typography>
                                <Box mt={1}>
                                    <SampleSummary sample={order.sample} />
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={4}>
                        <FileGrid files={order.files} />
                    </Box>
                    <Box display="flex" justifyContent="center" mb={4}>
                        <Box>
                            <LoadingButton
                                loading={createLoading || modifyLoading}
                                size="large"
                                variant="contained"
                                onClick={() => onSubmitOrderClick()}
                                sx={{ height: 50 }}
                            >
                                {order.inputType === OrderInputType.NEW
                                    ? translate('orders:SIGN_AND_SEND_ORDER')
                                    : translate('orders:UPDATE_ORDER')}
                            </LoadingButton>
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="center" maxWidth={900} margin="auto">
                        <Typography variant="h6" color="textSecondary" textAlign="center">
                            {translate('orders:ORDER_LEGAL_TEXT')}
                        </Typography>
                    </Box>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default ConfirmStep;
